import {
  Alert,
  Box,
  Button,
  FormGroup,
  Grid,
  IconButton,
  Modal,
  Typography,
} from "@mui/material";
import Crossicon from "../../Icon/Crossicon";
import CloseIcon from "@mui/icons-material/Close";
import { fontSize } from "../theme/fontSize";
import { fontWeight } from "../theme/fontWeight";
import { FontFamily } from "../theme/fontFamily";
import { ThemeColor } from "../theme/color";
import { Formik, Form } from "formik";
import CustomButton from "../button/button";
import OutlinedInputField from "../inputfiled/OutlinedInput";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  GetFilteredUserAction,
  GetUserChatAction,
} from "../../Redux/action/SettingAction";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import CustomSelect from "../select field/OutlinedSelectField";
import {
  AddGroupChatService,
  AddPersonChatService,
} from "../../Redux/service/SettingService";
import CustomizedFilterAutoComplete from "../autocomplete/FilterAutocomplete";
import MultiSelectAutocomplete from "../autocomplete/FilterAutocomplete";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "94%", sm: 520, md: 560 },
  maxWidth: "100%",
  maxHeight: "90vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 24px 60px rgba(16, 24, 40, 0.22)",
  p: { xs: 2.5, sm: 3 },
  outline: "none",
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb': { background: '#888', borderRadius: '10px' },
  '&::-webkit-scrollbar-thumb:hover': { background: '#555' },
};

export const CreateGroupChatModal = ({
  AddGroupChat,
  handleClose,
  stdId,
  Organizationlist,
  Rolelist,
}) => {
  const dispatch = useDispatch();

  const [Chatlist, setChatlist] = useState([]);

  const [formLoading, setFormLoading] = useState(false);

  const [filteredUser, setFilteredUser] = useState([]);

  const ChatlistState = useSelector(
    (state) => state.setting?.allchat?.data?.data
  );

  const FilteredUserlistState = useSelector(
    (state) => state.setting?.filteredUser?.data?.users
  );

  //console.log("filtered user", FilteredUserlistState);

  useEffect(() => {
    const updatedData = FilteredUserlistState?.map(
      ({ firstName, ...rest }) => ({
        title: firstName,
        ...rest,
      })
    )?.filter((data) => data._id !== window.sessionStorage.getItem("user_id"));
    setFilteredUser(updatedData);
  }, [FilteredUserlistState]);

  useEffect(() => {
    setChatlist(ChatlistState);
  }, [ChatlistState]);

  useEffect(() => {
    dispatch(GetFilteredUserAction());
  }, []);

  const validationSchemaGroup = Yup.object({
    selectedOptions: Yup.array()
      .required("Please select at least one user")
      .min(1, "Select at least one user to start a chat."),
    // A one-to-one chat carries no name of its own - each side sees the other
    // person. Only a group (2+ people picked, i.e. 3+ members) needs a name.
    groupName: Yup.string().when("selectedOptions", {
      is: (selectedOptions) => (selectedOptions?.length || 0) > 1,
      then: (schema) =>
        schema
          .trim()
          .required("Group name is required when chatting with more than one person."),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return (
    <Modal
      open={AddGroupChat}
      onClose={() => handleClose()}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            mb: 2.5,
          }}
        >
          <Typography
            fontFamily={FontFamily.Poppins}
            fontWeight={fontWeight.W600}
            fontSize={fontSize.h3.large}
            color={ThemeColor.BLACK}
          >
            Create Chat
          </Typography>
          <IconButton
            size="small"
            onClick={() => handleClose()}
            sx={{ color: "#667085" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Formik
          initialValues={{
            selectedOptions: [],
            groupName: "",
          }}
          onSubmit={async (values, { resetForm, setErrors }) => {
            const participants = (values.selectedOptions || []).map(
              (data) => data._id
            );

            // One person picked -> direct chat, check if a one-on-one chat already exists
            if (participants.length === 1) {
              const currentUserId = window.sessionStorage.getItem("user_id");
              const targetUserId = String(participants[0]);
              const alreadyExists = Chatlist?.some((chat) => {
                const pList = chat?.participants || [];
                const isDirect =
                  pList.length === 2 || chat?.isGroupChat === false;
                if (!isDirect) return false;
                const pIds = pList.map((p) => String(p?._id || p));
                return (
                  pIds.includes(targetUserId) &&
                  pIds.includes(String(currentUserId))
                );
              });

              if (alreadyExists) {
                setErrors({
                  selectedOptions:
                    "A one-on-one chat with this person already exists.",
                });
                dispatch(
                  showSnackbar(
                    "A one-on-one chat with this person already exists.",
                    "error"
                  )
                );
                return;
              }
            }

            setFormLoading(true);

            // One person picked -> direct chat, the server names it per viewer.
            // More than one -> a group, which needs the entered name.
            const payload =
              participants.length > 1
                ? { groupName: values.groupName?.trim(), participants }
                : { participants };

            await AddGroupChatService(payload)
              .then((res) => {
                if (res.status === 200) {
                  // //(res);
                  dispatch(GetUserChatAction());
                  setFormLoading(false);
                  handleClose();
                  dispatch(showSnackbar(res.data.message, "success"));

                  resetForm();
                }
              })
              .catch((e) => {
                setFormLoading(false);
                const errorMsg =
                  e?.response?.data?.message || "Failed to create chat";
                if (
                  participants.length === 1 &&
                  errorMsg.toLowerCase().includes("already exists")
                ) {
                  setErrors({
                    selectedOptions: errorMsg,
                  });
                }
                dispatch(showSnackbar(errorMsg, "error"));
              });
          }}
          validationSchema={validationSchemaGroup}
        >
          {({
            values,
            errors,
            handleBlur,
            handleChange,
            touched,
            handleSubmit,
            setFieldValue,
          }) => (
            <Form className="w-100">
              <Box sx={{ width: "100%" }}>
                <FormGroup>
                  <MultiSelectAutocomplete
                    options={filteredUser}
                    label="Select user..."
                    filtertext={true}
                    value={values.selectedOptions}
                    onChange={(newValue) => {
                      //console.log(newValue);
                      // dispatch(GetFilteredUserAction());

                      setFieldValue("selectedOptions", newValue);
                    }}
                  />
                  {errors.selectedOptions && (
                    <div className="mt-2" style={{ color: "red" }}>
                      {errors.selectedOptions}
                    </div>
                  )}
                </FormGroup>
                {/* <Box sx={{ my: 2 }}>
                  {values?.selectedOptions.length == 1 ||
                  values?.selectedOptions.length == 0 ? null : (
                    <Alert severity="info">
                      {"Looks like you are creating a group"}
                    </Alert>
                  )}
                </Box> */}

                {values?.selectedOptions?.length > 1 ? (
                  <FormGroup sx={{ mt: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "#344054",
                        mb: 0.75,
                      }}
                    >
                      Group name
                    </Typography>
                    <OutlinedInputField
                      value={values.groupName}
                      name={"groupName"}
                      onChange={handleChange}
                      placeholder="Name this group..."
                      fullWidth={true}
                    />
                    {errors.groupName && (
                      <div className="mt-1" style={{ color: "red", fontSize: 13 }}>
                        {errors.groupName}
                      </div>
                    )}
                  </FormGroup>
                ) : values?.selectedOptions?.length === 1 ? (
                  <Alert severity="info" sx={{ mt: 2, borderRadius: "10px" }}>
                    This will be a one-to-one chat &mdash; each of you will see
                    the other person's name.
                  </Alert>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column-reverse", sm: "row" },
                    justifyContent: "flex-end",
                    gap: 1.5,
                    mt: 3,
                    "& .MuiButton-root": { width: { xs: "100%", sm: "auto" } },
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => handleClose()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "10px",
                      color: "#667085",
                      borderColor: "#d0d5dd",
                      "&:hover": {
                        borderColor: "#98a2b3",
                        backgroundColor: "#f9fafb",
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <CustomButton
                    variant="contained"
                    type="submit"
                    backgroundColor={ThemeColor.PURPLE}
                    color={ThemeColor.WHITE}
                    loading={formLoading}
                  >
                    Create Chat
                  </CustomButton>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </Modal>
  );
};
